import React from 'react';

import { MarkdownText } from './MarkdownText';

export type ExpandableTextProps = {
  text: string;
  /** Number of visible lines while collapsed */
  lines?: number;
  className?: string;
  /** Render the text as Markdown instead of plain pre-wrapped text */
  markdown?: boolean;
};

/**
 * Multi-line text that is clamped with a trailing ellipsis when it overflows,
 * with a toggle shown only when there is something to expand.
 */
export function ExpandableText({ text, lines = 3, className, markdown = false }: ExpandableTextProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = React.useState(false);
  const [overflowing, setOverflowing] = React.useState(false);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      // While expanded the clamp is off, so keep the last known overflow state.
      if (expanded) return;
      setOverflowing(el.scrollHeight > el.clientHeight + 1);
    };

    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, lines, expanded]);

  return (
    <div className={`ttbExpandableText${className ? ` ${className}` : ''}`}>
      <div
        ref={ref}
        className="ttbExpandableTextBody"
        style={expanded ? undefined : { WebkitLineClamp: lines }}
        data-clamped={expanded ? undefined : 'true'}
        data-markdown={markdown ? 'true' : undefined}
      >
        {markdown ? <MarkdownText text={text} /> : text}
      </div>
      {overflowing || expanded ? (
        <button
          type="button"
          className="ttbExpandableTextToggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Свернуть' : 'Показать полностью'}
        </button>
      ) : null}
    </div>
  );
}
